import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRequest } from '~/lib/useRequest';
import { config } from '~/config';
import { CourseMappingResource } from '@ab/ams-school-api/types/private/v1';
import { hasKey } from '~/utils';

export type UpdateCourseMappingPayload = {
  id: string;
  equivalentCourse: string;
  sourceCourseName: string;
  sourceCountryCode?: string | null;
  sourceEducationLevel?: string | null;
  sourceInstitutionName?: string | null;
  sourceProgramName?: string | null;
};

export type UpdateCourseMappingResponse = {
  data: CourseMappingResource;
};

export function useUpdateCourseMapping() {
  const request = useRequest();
  const queryClient = useQueryClient();

  const { isLoading, error, mutateAsync } = useMutation(
    ['updateCourseMapping'],
    async (payload: UpdateCourseMappingPayload) => {
      const { id, ...attributes } = payload;
      const response = await request<UpdateCourseMappingResponse>(
        `${config.apiHost}/private/v1/course_mappings/${id}`,
        {
          method: 'PATCH',
          body: {
            data: {
              type: 'course_mapping',
              id,
              attributes,
            },
          },
          isExpectedResponse,
        }
      );

      // Invalidate queries to refresh the course mappings list
      queryClient.invalidateQueries({ queryKey: ['courseMappings'] });

      return response.data;
    }
  );

  return {
    updateCourseMapping: mutateAsync,
    isUpdating: isLoading,
    error,
  };
}

function isExpectedResponse(res: unknown): res is UpdateCourseMappingResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}
